/**
 * Single source of truth for "what kinds of quote/itinerary components exist,
 * and how are they added/edited" - shared by QuoteBuilder.js (editing a
 * quote) and EditItineraryModal.jsx (editing an itinerary, which is a
 * straight JSON copy of a quote's components made at quote->itinerary
 * conversion - see backend/routes/itineraries.js POST /convert-quote/:quoteId).
 *
 * Before this module existed, EditItineraryModal.jsx had its own separate,
 * much simpler Type/Title/Description/Price-only component editor that (a)
 * replaced the whole component object on save, silently discarding every
 * type-specific `data.*` field (departure/return dates, airports, tripType,
 * etc.), isOption/photos/documents, and Voyager AI's requestedTimeframe/
 * needsDatesConfirmed reminder fields, and (b) used different `type` values
 * ("Flight" vs "flight") than everywhere else in the app, breaking the
 * client-facing type checks in ModernQuoteView.js/ClientPortalView.jsx for
 * any component edited there. Extracting the real logic here so both
 * editors use the exact same forms/behavior prevents that kind of drift.
 */
import CruiseForm from './components/CruiseForm';
import FlightForm from './components/FlightForm';
import HotelForm from './components/HotelForm';
import CarRentalForm from './components/CarRentalForm';
import RestaurantForm from './components/RestaurantForm';
import PackageDealForm from './components/PackageDealForm';
import GeneralItemForm from './components/GeneralItemForm';
import InsuranceForm from './components/InsuranceForm';
import DisneyThemeParkPackageForm from './components/DisneyThemeParkPackageForm';
import DisneylandResortPackageForm from './components/DisneylandResortPackageForm';
import DisneyCruiseLineForm from './components/DisneyCruiseLineForm';
import RoyalCaribbeanCruiseForm from './components/RoyalCaribbeanCruiseForm';
import CarnivalCruiseForm from './components/CarnivalCruiseForm';
import UniversalOrlandoPackageForm from './components/UniversalOrlandoPackageForm';
import UniversalHollywoodPackageForm from './components/UniversalHollywoodPackageForm';
import ActivityForm from './components/ActivityForm';
import { primaryOptionFromComponent } from './optionUtils';

// Component categories and types - the canonical list of every component
// `type` value the app understands (must stay in sync with the `case`s in
// renderComponentTypeForm below, and with backend/utils/
// conciergeLeadToQuoteComponent.js's CATEGORY_TO_COMPONENT_TYPE map).
export const componentCategories = [
  {
    id: 'general',
    name: 'General Travel Components',
    icon: '📋',
    description: 'Non-brand specific travel services',
    components: [
      { id: 'hotel', name: 'Hotel', icon: '🏨' },
      { id: 'flight', name: 'Flight', icon: '✈️' },
      { id: 'car', name: 'Car Rental', icon: '🚗' },
      { id: 'restaurant', name: 'Restaurant', icon: '🍽️' },
      { id: 'transfer', name: 'Airport Transfer', icon: '🚌' },
      { id: 'package', name: 'Package Deal', icon: '📦' },
      { id: 'insurance', name: 'Travel Insurance', icon: '🛡️' },
      { id: 'general', name: 'General Item', icon: '📋' }
    ]
  },
  {
    id: 'cruises',
    name: 'Cruise Lines',
    icon: '🚢',
    description: 'Cruise packages and experiences',
    components: [
      { id: 'cruise', name: 'General Cruise', icon: '🚢' },
      { id: 'carnival-cruise', name: 'Carnival Cruise Line', icon: '🎪' },
      { id: 'royal-caribbean-cruise', name: 'Royal Caribbean', icon: '⚓' },
      { id: 'norwegian-cruise', name: 'Norwegian Cruise Line', icon: '🇳🇴' },
      { id: 'disney-cruise-line', name: 'Disney Cruise Line', icon: '🐭' },
      { id: 'celebrity-cruise', name: 'Celebrity Cruises', icon: '⭐' },
      { id: 'princess-cruise', name: 'Princess Cruises', icon: '👸' },
      { id: 'msc-cruise', name: 'MSC Cruises', icon: '�' }
    ]
  },
  {
    id: 'theme-parks',
    name: 'Theme Parks & Experiences',
    icon: '�',
    description: 'Branded resort and theme park packages',
    components: [
      { id: 'disney-wdw-resort-package', name: 'Walt Disney World Package', icon: '�' },
      { id: 'universal-orlando-package', name: 'Universal Orlando Package', icon: '🎢' },
      { id: 'universal-hollywood-package', name: 'Universal Hollywood Package', icon: '�' },
      { id: 'disneyland-resort-package', name: 'Disneyland Resort', icon: '✨' },
      { id: 'seaworld', name: 'SeaWorld Package', icon: '🐋' },
      { id: 'busch-gardens', name: 'Busch Gardens', icon: '🎡' }
    ]
  },
  {
    id: 'activities',
    name: 'Activities',
    icon: '🎟️',
    description: 'Tickets, tours, and standalone experiences',
    components: [
      { id: 'tickets', name: 'Tickets / Admission', icon: '🎟️' },
      { id: 'tour', name: 'Tour / Excursion', icon: '🗺️' },
      { id: 'spa', name: 'Spa / Wellness', icon: '🧖' },
      { id: 'entertainment', name: 'Show / Entertainment', icon: '🎭' },
      { id: 'sport', name: 'Sports Activity', icon: '⛷️' },
      { id: 'culinary', name: 'Culinary Experience', icon: '👨‍🍳' }
    ]
  }
];

// Default field values to prefill when an agent picks one of the catalog
// shortcuts above that shares an underlying Form component with other
// entries (e.g. every cruise-line button opens the same CruiseForm, every
// Activities-category button opens the same ActivityForm) - see
// renderComponentTypeForm below. Deliberately NOT introducing brand-new
// `type` values for these (they still save as 'cruise'/'activity'/'general')
// to avoid the pdfQuoteParser.js/conciergeLeadToQuoteComponent.js type-ID
// drift problem documented in AGENTS.md - only the pre-filled label differs.
export const componentTypeDefaults = {
  'norwegian-cruise': { carrier: 'Norwegian Cruise Line' },
  'celebrity-cruise': { carrier: 'Celebrity Cruises' },
  'princess-cruise': { carrier: 'Princess Cruises' },
  'msc-cruise': { carrier: 'MSC Cruises' },
  'transfer': { itemName: 'Airport Transfer' },
  'seaworld': { activityType: 'SeaWorld Package' },
  'busch-gardens': { activityType: 'Busch Gardens' },
  'tickets': { activityType: 'Tickets / Admission' },
  'tour': { activityType: 'Tour / Excursion' },
  'spa': { activityType: 'Spa / Wellness' },
  'entertainment': { activityType: 'Show / Entertainment' },
  'sport': { activityType: 'Sports Activity' },
  'culinary': { activityType: 'Culinary Experience' }
};

// Flatten component types for backward compatibility
export const componentTypes = componentCategories.flatMap(category => category.components);

// Infer a component's type from its title/description when `type` is
// missing (e.g. very old components saved before `type` was stored).
export function inferComponentType(component) {
  if (!component) return null;

  const title = (component.title || '').toLowerCase();
  const description = (component.description || '').toLowerCase();
  const text = `${title} ${description}`;

  if (text.includes('flight') || text.includes('airline') || text.includes('air')) return 'flight';
  if (text.includes('hotel') || text.includes('resort') || text.includes('accommodation')) return 'hotel';
  if (text.includes('cruise') || text.includes('ship')) return 'cruise';
  if (text.includes('car') || text.includes('rental') || text.includes('vehicle')) return 'car';
  if (text.includes('restaurant') || text.includes('dining') || text.includes('meal')) return 'restaurant';
  if (text.includes('insurance') || text.includes('protection') || text.includes('coverage')) return 'insurance';
  if (text.includes('activity') || text.includes('tour') || text.includes('ticket') || text.includes('excursion')) return 'activity';
  if (text.includes('package') || text.includes('deal') || text.includes('bundle')) return 'package';
  if (text.includes('disney') || text.includes('theme park') || text.includes('wdw')) return 'disney-wdw-resort-package';

  return 'general';
}

// Prepare a saved component for re-editing: merge the top-level fields every
// component shares with the nested type-specific `data` fields, since each
// Form component reads both off a single flat `initialData` prop.
export function getComponentFormInitialData(editingComponent) {
  if (!editingComponent) return null;
  return {
    title: editingComponent.title,
    description: editingComponent.description,
    price: editingComponent.price,
    depositRequired: editingComponent.depositRequired,
    depositAmount: editingComponent.depositAmount,
    depositDueDate: editingComponent.depositDueDate,
    photos: editingComponent.photos || [],
    documents: editingComponent.documents || [],
    isOption: editingComponent.isOption || false,
    optionDescription: editingComponent.optionDescription || '',
    paymentTiming: editingComponent.paymentTiming || 'with_deposit',
    optionalDepositAmount: editingComponent.optionalDepositAmount ?? '',
    // Quote Builder Phase 2 - internal-only notes/commission on the
    // primary option (see optionUtils.js). New, optional; absent on any
    // component saved before this feature, which is fine since these
    // default to blank/null exactly like a never-filled-in field would.
    agentNotes: editingComponent.agentNotes || '',
    agentCommission: editingComponent.agentCommission ?? null,
    // Agent-only Voyager AI reminder fields (see QuoteBuilder.js's
    // needsDateReminder) - preserved across edits rather than dropped.
    requestedTimeframe: editingComponent.requestedTimeframe,
    needsDatesConfirmed: editingComponent.needsDatesConfirmed,
    ...(editingComponent.data || {})
  };
}

// Render the correct form for a given component `type`. `props` is
// `{ onSave, onCancel, initialData }` - the same shape every Form component
// (FlightForm, HotelForm, etc.) expects.
export function renderComponentTypeForm(effectiveComponentType, props) {
  switch (effectiveComponentType) {
    case 'cruise':
    case 'norwegian-cruise':
    case 'celebrity-cruise':
    case 'princess-cruise':
    case 'msc-cruise':
      return <CruiseForm {...props} />;
    case 'flight':
      return <FlightForm {...props} />;
    case 'hotel':
      return <HotelForm {...props} />;
    case 'car':
      return <CarRentalForm {...props} />;
    case 'restaurant':
      return <RestaurantForm {...props} />;
    case 'package':
      return <PackageDealForm {...props} />;
    case 'general':
    case 'transfer':
      return <GeneralItemForm {...props} />;
    case 'insurance':
      return <InsuranceForm {...props} />;
    case 'activity':
    case 'seaworld':
    case 'busch-gardens':
    case 'tickets':
    case 'tour':
    case 'spa':
    case 'entertainment':
    case 'sport':
    case 'culinary':
      return <ActivityForm {...props} />;
    case 'disney-wdw-resort-package':
      return <DisneyThemeParkPackageForm {...props} />;
    case 'disneyland-resort-package':
      return <DisneylandResortPackageForm {...props} />;
    case 'disney-cruise-line':
      return <DisneyCruiseLineForm {...props} />;
    case 'royal-caribbean-cruise':
      return <RoyalCaribbeanCruiseForm {...props} />;
    case 'carnival-cruise':
      return <CarnivalCruiseForm {...props} />;
    case 'universal-orlando-package':
      return <UniversalOrlandoPackageForm {...props} />;
    case 'universal-hollywood-package':
      return <UniversalHollywoodPackageForm {...props} />;
    default:
      return null;
  }
}

// Turn a Form component's onSave payload into the finalized component object
// that goes into `quote.components`/`itinerary.components`. Handles both the
// "adding a new component" and "editing an existing one" cases identically
// everywhere it's called, so a component built via Voyager AI, the Quote
// Builder, or the Itinerary editor all end up with exactly the same shape.
export function buildSavedComponent({ componentData, selectedComponentType, editingComponent }) {
  const {
    title, description, price, depositAmount, depositRequired, depositDueDate,
    photos, documents, type, data, isOption, optionDescription,
    paymentTiming, optionalDepositAmount, agentNotes, agentCommission,
    requestedTimeframe, needsDatesConfirmed,
    ...otherFields
  } = componentData;

  const common = {
    type: type || selectedComponentType || editingComponent?.type,
    title,
    description: description || '',
    price: parseFloat(price) || 0,
    depositAmount: parseFloat(depositAmount) || 0,
    depositRequired: depositRequired || false,
    depositDueDate: depositDueDate || '',
    photos: photos || [],
    documents: documents || [],
    isOption: isOption || false,
    optionDescription: optionDescription || '',
    paymentTiming: paymentTiming || 'with_deposit',
    optionalDepositAmount: optionalDepositAmount === '' || optionalDepositAmount === undefined ? null : parseFloat(optionalDepositAmount),
    // Quote Builder Phase 2 (see optionUtils.js) - internal-only, informational.
    agentNotes: agentNotes || '',
    agentCommission: agentCommission === '' || agentCommission === undefined || agentCommission === null ? null : parseFloat(agentCommission)
  };

  const saved = editingComponent
    ? {
        ...editingComponent,
        ...common,
        // Preserve the Voyager AI reminder unless the form explicitly changed
        // it (none of the real Form components touch these fields).
        requestedTimeframe: requestedTimeframe !== undefined ? requestedTimeframe : editingComponent.requestedTimeframe,
        needsDatesConfirmed: needsDatesConfirmed !== undefined ? needsDatesConfirmed : editingComponent.needsDatesConfirmed,
        // Use the data field directly if provided, otherwise use remaining fields
        data: data || otherFields,
        updatedAt: new Date().toISOString()
      }
    : {
        id: Date.now().toString(),
        ...common,
        data: data || otherFields,
        createdAt: new Date().toISOString()
      };

  // Quote Builder Phase 2: keep options[0] ("primary" option) in sync with
  // the top-level fields every form already saves above, and preserve any
  // additional options[1+] an agent added via "Add Option" untouched. This
  // is purely additive - nothing reads `options` unless it explicitly wants
  // the multi-choice comparison view (see optionUtils.getComponentOptions),
  // so a component that's never had an option added still behaves exactly
  // as it always has.
  const existingAdditionalOptions = Array.isArray(editingComponent?.options) ? editingComponent.options.slice(1) : [];
  saved.options = [primaryOptionFromComponent(saved), ...existingAdditionalOptions];

  return saved;
}
